import { CalendarClock, Laptop } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/common/PageHero";
import { PillLink } from "@/components/common/PillLink";
import { Reveal } from "@/components/common/Reveal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { COURSES, ONLINE_TIMETABLE } from "@/lib/site";

const TBC = "To be confirmed";
const courseOf = (slug) => COURSES.find((c) => c.slug === slug);

// Real sessions from ONLINE_TIMETABLE; until they are added, one "to be confirmed" row per course
const rows = ONLINE_TIMETABLE.length
  ? ONLINE_TIMETABLE
  : COURSES.map((c) => ({ course: c.slug, years: TBC, day: TBC, time: TBC, note: "" }));

const Pending = () => (
  <div className="mb-6 flex items-start gap-3 rounded-card bg-lemon p-5" role="status" data-testid="timetable-pending">
    <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
    <p className="leading-relaxed">
      <span className="font-bold">The online timetable is being finalised.</span> Send us an enquiry and we'll share the current
      class times for your learner's year level.
    </p>
  </div>
);

export default function OnlineTimetable() {
  useSeo("Online Timetable", "Online class timetable for MUCCI Academy courses, for learners anywhere in New Zealand.");
  return (
    <div data-testid="online-timetable-page">
      <PageHero testid="online-hero" eyebrow="Online across NZ" color="bg-mint" title="Online class timetable"
        intro="Join MUCCI Academy classes online from anywhere in New Zealand. Times are shown in New Zealand time." />
      <section className="container-x section-y" aria-labelledby="timetable-title">
        <Reveal>
          <h2 id="timetable-title" className="flex items-center gap-3 text-3xl sm:text-4xl font-semibold">
            <Laptop className="h-8 w-8" aria-hidden="true" /> Online classes
          </h2>
        </Reveal>
        <div className="mt-8">
          {!ONLINE_TIMETABLE.length && <Pending />}
          <div className="card-soft overflow-x-auto" data-testid="timetable-table">
            <Table>
              <TableHeader>
                <TableRow>
                  {["Course", "Year levels", "Day", "Time (NZT)"].map((h) => (
                    <TableHead key={h} className="py-4 font-bold text-ink">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => {
                  const c = courseOf(r.course);
                  return (
                    <TableRow key={`${r.course}-${i}`} data-testid={`timetable-row-${i}`}>
                      <TableCell className="py-4">
                        <span className={`inline-block rounded-full px-3 py-1 font-bold ${c?.color || "bg-white"}`}>{c?.title || r.course}</span>
                        {r.note && <div className="mt-1 text-sm text-ink-muted">{r.note}</div>}
                      </TableCell>
                      <TableCell className={r.years === TBC ? "text-ink-muted" : ""}>{r.years}</TableCell>
                      <TableCell className={r.day === TBC ? "text-ink-muted" : ""}>{r.day}</TableCell>
                      <TableCell className={r.time === TBC ? "text-ink-muted" : ""}>{r.time}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <PillLink to="/contact#enquire" variant="lemon" arrow data-testid="timetable-enquire">Enquire about online classes</PillLink>
          <PillLink to="/courses" variant="mint" arrow data-testid="timetable-courses">See the courses</PillLink>
        </div>
      </section>
    </div>
  );
}
